import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { getCurrentUser } from '@/shared/services/user.service';
import { showToast } from '@/shared/utils/sweatAlertCustomHelper'
import { cleanDate } from '@/shared/utils/generalUtils'
import { getAccessTokenToCookies, getAccessToken, getUserIdFromToken, validateTokenUserId, getActiveStorageType } from '@/shared/utils/tokenHelper'

export const userStore = defineStore('userStore', () => {
  // Storage keys for persistence - now user-specific to prevent cross-user conflicts
  const STORAGE_KEY_PREFIX = 'userData';
  const STORAGE_TIMESTAMP_PREFIX = 'userDataTimestamp';
  const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache duration (reduced from 5 for better security)
  
  // Sensitive fields that should NOT be cached
  const SENSITIVE_FIELDS = ['email', 'phoneNumber', 'address', 'dateOfBirth'];
  
  // Safe fields that can be cached for display purposes
  const SAFE_CACHE_FIELDS = [
    'id', 'username', 'firstName', 'lastName', 
    'profilePictureUrl', 'coverImageUrl', 
    'postsCount', 'friendsCount', 'banierDeco',
    'profilePostId', 'coverPostId', 'gender', 'maritalStatus'
  ];

  // Helper to get user-specific storage keys
  function getStorageKeys(userId) {
    if (!userId) return { dataKey: null, timestampKey: null };
    return {
      dataKey: `${STORAGE_KEY_PREFIX}_${userId}`,
      timestampKey: `${STORAGE_TIMESTAMP_PREFIX}_${userId}`
    };
  }

  const defualtUserObject = {
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    bio: "",
    gender: "",
    dateOfBirth: "",
    maritalStatus: "",
    state: "",
    address: "",
    isEmailVerified: false,
    isActive: false,
    privacySetting: "",
    createdAt: "",
    lastLogin: "",
    postsCount: 0,
    friendsCount: 0,
    banierDeco: null,
    profilePostId: null,
    coverPostId: null,
    isCertified: false,
  }
  const user = reactive({ ...defualtUserObject })

  const isLoading = ref(false); // Start as false, only set to true when actually fetching
  const isUserFetched = ref(false);
  let fetchPromise = null; // Track ongoing fetch to prevent duplicates

  /**
   * Determine which storage to use based on rememberMe setting
   * localStorage = rememberMe true, sessionStorage = rememberMe false
   * SECURITY: Uses sessionStorage when rememberMe is false to auto-clear on browser close
   */
  function getStorageForCache() {
    const activeStorageType = getActiveStorageType();
    // If using localStorage for tokens (rememberMe = true), use localStorage for cache
    // If using sessionStorage for tokens (rememberMe = false), use sessionStorage for cache
    // This ensures cache is cleared when browser closes if user didn't check "remember me"
    return activeStorageType === 'localStorage' ? localStorage : sessionStorage;
  }

  /**
   * HYDRATION: Restore user data from storage immediately on store initialization
   * SECURITY: Now checks both localStorage and sessionStorage, uses appropriate one based on rememberMe
   * CRITICAL FIX: Now uses user-specific storage keys based on token userId
   * MULTI-USER FIX: Validates token belongs to current user before using cache
   */
  function hydrateFromStorage() {
    try {
      // First, get userId from token to load the correct user's cache
      const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
      
      // MULTI-USER FIX: Try to get token, but validate it belongs to the user
      // First, try to get token without validation
      let token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();
      
      if (!token) {
        // No token - clear any stale cache
        clearStorageForUser(null);
        return false;
      }

      // Extract userId from token
      const tokenUserId = getUserIdFromToken(token);
      if (!tokenUserId) {
        // Invalid token - clear cache
        clearStorageForUser(null);
        return false;
      }

      // CRITICAL MULTI-USER FIX: Validate token belongs to extracted userId
      // This prevents using a token from a different user in a different window
      if (!validateTokenUserId(token, tokenUserId)) {
        // Token doesn't belong to expected user - clear cache
        clearStorageForUser(null);
        return false;
      }

      // Get user-specific storage keys
      const { dataKey, timestampKey } = getStorageKeys(tokenUserId);
      if (!dataKey || !timestampKey) return false;

      // Check both storages (localStorage first, then sessionStorage)
      // This handles cases where user switched between rememberMe settings
      let stored = null;
      let timestamp = null;
      let storage = null;

      // Try localStorage first (rememberMe = true)
      const localStored = localStorage.getItem(dataKey);
      const localTimestamp = localStorage.getItem(timestampKey);
      
      // Try sessionStorage (rememberMe = false)
      const sessionStored = sessionStorage.getItem(dataKey);
      const sessionTimestamp = sessionStorage.getItem(timestampKey);

      // Prefer the storage that matches current token storage type
      const preferredStorage = getStorageForCache();
      if (preferredStorage === localStorage && localStored && localTimestamp) {
        stored = localStored;
        timestamp = localTimestamp;
        storage = localStorage;
      } else if (preferredStorage === sessionStorage && sessionStored && sessionTimestamp) {
        stored = sessionStored;
        timestamp = sessionTimestamp;
        storage = sessionStorage;
      } else if (localStored && localTimestamp) {
        // Fallback to localStorage if available
        stored = localStored;
        timestamp = localTimestamp;
        storage = localStorage;
      } else if (sessionStored && sessionTimestamp) {
        // Fallback to sessionStorage if available
        stored = sessionStored;
        timestamp = sessionTimestamp;
        storage = sessionStorage;
      }

      if (stored && timestamp && storage) {
        const age = Date.now() - Number.parseInt(timestamp, 10);

        // Use cached data if it's fresh (less than cache duration)
        if (age < CACHE_DURATION) {
          try {
            const userData = JSON.parse(stored);
            // CRITICAL: Validate cached user ID matches token userId
            if (userData.id && userData.id === tokenUserId) {
              // Only assign cached safe fields, sensitive fields will be fetched from API
              const safeData = {};
              SAFE_CACHE_FIELDS.forEach(field => {
                if (userData[field] !== undefined) {
                  safeData[field] = userData[field];
                }
              });
              Object.assign(user, safeData);
              isUserFetched.value = false; // Mark as not fully fetched (sensitive data missing)
              return true; // Return true to indicate partial data is available
            } else {
              // Cached user doesn't match token - clear it
              clearStorageForUser(tokenUserId);
            }
          } catch (parseError) {
            // Invalid JSON - clear cache
            clearStorageForUser(tokenUserId);
          }
        } else {
          // Cache expired, clear it
          clearStorageForUser(tokenUserId);
        }
      }
    } catch (error) {
      // On any error, clear cache
      const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
      const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();
      const tokenUserId = token ? getUserIdFromToken(token) : null;
      clearStorageForUser(tokenUserId);
    }
    return false;
  }

  /**
   * PERSIST: Save user data to storage for instant hydration on next page load
   * SECURITY: Only caches non-sensitive fields, uses sessionStorage if rememberMe is false
   * CRITICAL FIX: Now uses user-specific storage keys to prevent cross-user conflicts
   */
  function persistToStorage(userData) {
    try {
      if (!userData || !userData.id) {
        // Can't persist without user ID
        return;
      }

      // SECURITY: Only cache safe, non-sensitive fields
      const safeData = {};
      SAFE_CACHE_FIELDS.forEach(field => {
        if (userData[field] !== undefined) {
          safeData[field] = userData[field];
        }
      });

      // Explicitly exclude sensitive fields (double-check)
      SENSITIVE_FIELDS.forEach(field => {
        delete safeData[field];
      });

      const { dataKey, timestampKey } = getStorageKeys(userData.id);
      if (!dataKey || !timestampKey) return;

      // Use appropriate storage based on rememberMe setting
      const storage = getStorageForCache();
      
      storage.setItem(dataKey, JSON.stringify(safeData));
      storage.setItem(timestampKey, Date.now().toString());
      
      // Clean up from other storage if it exists (user switched rememberMe setting)
      const otherStorage = storage === localStorage ? sessionStorage : localStorage;
      if (otherStorage.getItem(dataKey)) {
        otherStorage.removeItem(dataKey);
        otherStorage.removeItem(timestampKey);
      }
    } catch (error) {
      // Storage might be full or disabled - continue without persistence
    }
  }

  /**
   * Clear persisted user data from storage for a specific user
   * SECURITY: Clears from both localStorage and sessionStorage
   * CRITICAL FIX: Now clears user-specific cache
   */
  function clearStorageForUser(userId) {
    try {
      if (userId) {
        // Clear specific user's cache from both storages
        const { dataKey, timestampKey } = getStorageKeys(userId);
        if (dataKey) {
          localStorage.removeItem(dataKey);
          sessionStorage.removeItem(dataKey);
        }
        if (timestampKey) {
          localStorage.removeItem(timestampKey);
          sessionStorage.removeItem(timestampKey);
        }
      } else {
        // Clear all user caches from both storages (fallback for cleanup)
        // This is a safety measure - normally we should have userId
        [localStorage, sessionStorage].forEach(storage => {
          const keys = Object.keys(storage);
          keys.forEach(key => {
            if (key.startsWith(STORAGE_KEY_PREFIX + '_') || key.startsWith(STORAGE_TIMESTAMP_PREFIX + '_')) {
              storage.removeItem(key);
            }
          });
        });
      }
    } catch (error) {
      // Failed to clear storage
    }
  }

  /**
   * Cleanup old cache entries to prevent storage from filling up
   * SECURITY: Removes cache entries older than 24 hours from both storages
   */
  function cleanupOldCacheEntries() {
    try {
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      const now = Date.now();

      // Clean up both localStorage and sessionStorage
      [localStorage, sessionStorage].forEach(storage => {
        const keys = Object.keys(storage);
        keys.forEach(key => {
          if (key.startsWith(STORAGE_TIMESTAMP_PREFIX + '_')) {
            const timestamp = storage.getItem(key);
            if (timestamp) {
              const age = now - Number.parseInt(timestamp, 10);
              if (age > maxAge) {
                // Cache is older than 24 hours - remove it and its data
                const userId = key.replace(STORAGE_TIMESTAMP_PREFIX + '_', '');
                clearStorageForUser(userId);
              }
            }
          }
        });
      });
    } catch (error) {
      // Failed to cleanup - continue silently
    }
  }

  /**
   * Clear persisted user data from localStorage (backward compatibility)
   */
  function clearStorage() {
    // Get current user ID from token if available
    const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
    const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();
    const tokenUserId = token ? getUserIdFromToken(token) : null;
    clearStorageForUser(tokenUserId);
  }

  // ✅ Initialize: Try to hydrate from cache immediately
  hydrateFromStorage();

  // Getters
  function getUser() {
    return user;
  }

  // Setters
  function updateUser(userData) {
    Object.assign(user, userData);
  }

  function reset() {
    // Get current user ID before clearing (if available)
    // Try multiple sources: user.id, then token, as fallback
    let currentUserId = user.id || null;
    
    // Fallback: try to get userId from token if user.id is not available
    if (!currentUserId) {
      try {
        const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
        const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();
        if (token) {
          currentUserId = getUserIdFromToken(token);
        }
      } catch (error) {
        // Ignore errors - we'll just clear all cache if no userId found
      }
    }
    
    // Clear user data
    Object.assign(user, defualtUserObject);
    
    // Clear persisted data for current user (or all if no userId)
    clearStorageForUser(currentUserId);
    
    isLoading.value = false;
    isUserFetched.value = false;
    fetchPromise = null;
  }

  async function fetchCurrentUser(forceRefresh = false) {
    // If already fetching and not forcing refresh, return the existing promise
    if (fetchPromise && !forceRefresh) {
      return fetchPromise;
    }

    // Check if token exists before making the request
    const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
    let token = null;
    let tokenUserId = null;
    
    if (isUseCookies) {
      // For cookie-based auth, httpOnly cookies cannot be read by JavaScript
      // So we can't check token existence here - just make the API call
      // The backend will validate cookies and return 401 if invalid
      // If we have cached user data, use it for validation
      if (user.id) {
        tokenUserId = user.id;
      }
    } else {
      // For token-based auth, we can read the token from storage
      token = getAccessToken();
      
      if (!token) {
        // No token - reset user data to prevent showing stale data
        reset();
        return;
      }

      // Extract userId from token for validation
      tokenUserId = getUserIdFromToken(token);
      if (!tokenUserId) {
        reset();
        return;
      }
      
      // CRITICAL MULTI-USER FIX: If we have a cached user, validate token matches
      if (user.id && user.id !== tokenUserId) {
        // Token belongs to different user - this is a conflict
        // Clear old user's cache and reset
        clearStorageForUser(user.id);
        Object.assign(user, defualtUserObject);
        isUserFetched.value = false;
      }
    }

    // Check if we have valid cached data for THIS user and don't need to force refresh
    // For cookie-based auth: if we have cached user data, we can use it (tokenUserId may be null)
    // For token-based auth: validate tokenUserId matches cached user
    const hasValidCache = !forceRefresh && isUserFetched.value && user.id && (
      isUseCookies || // Cookie-based: use cache if user.id exists
      (tokenUserId && user.id === tokenUserId) // Token-based: validate match
    );
    
    if (hasValidCache) {
      const { timestampKey } = getStorageKeys(user.id);
      if (timestampKey) {
        // Check both storages
        const storage = getStorageForCache();
        const timestamp = storage.getItem(timestampKey);
        if (timestamp) {
          const age = Date.now() - Number.parseInt(timestamp, 10);
          if (age < CACHE_DURATION) {
            // Cache is still valid for this user, but we still need to fetch
            // to get sensitive fields that weren't cached
            // Return cached data but mark that full fetch is needed
            return Promise.resolve({ success: true, data: user });
          }
        }
      }
    }

    isLoading.value = true;

    // Create promise with timeout protection
    fetchPromise = (async () => {
      try {
        // Add timeout to prevent hanging forever
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });

        const result = await Promise.race([
          getCurrentUser(),
          timeoutPromise
        ]);

        if (result.success && result.data) {
          // CRITICAL: Validate that fetched user ID matches token userId (if we have tokenUserId)
          // This prevents cross-user data conflicts
          if (tokenUserId && result.data.id && result.data.id !== tokenUserId) {
            // Token doesn't match fetched user - clear everything
            reset();
            isLoading.value = false;
            isUserFetched.value = false;
            return;
          }

          // Validate token is still valid before storing user data (only for token-based auth)
          // For cookie-based auth, backend already validated the cookies
          if (!isUseCookies) {
            const currentToken = getAccessToken();
            if (!currentToken || !validateTokenUserId(currentToken, result.data.id)) {
              reset();
              isLoading.value = false;
              isUserFetched.value = false;
              return;
            }
          }

          result.data.dateOfBirth = cleanDate(result.data.dateOfBirth);
          Object.assign(user, result.data);
          persistToStorage(result.data); // ✅ Persist after successful fetch
          isLoading.value = false;
          isUserFetched.value = true;
        } else {
          // Request failed - check if it's an auth error
          const errorMessage = (result.message || '').toLowerCase();
          const isAuthError = errorMessage.includes('unauthorized') ||
            errorMessage.includes('token') ||
            errorMessage.includes('no token found') ||
            errorMessage.includes('session expired') ||
            errorMessage.includes('authentication');
          
          // If it's an auth error, reset user data (API interceptor should handle logout)
          // But we reset here to prevent showing stale data
          if (isAuthError) {
            reset();
          }
          
          // Only show error toast if it's not an auth error
          // Auth errors are expected after logout/invalid session and shouldn't show popups
          if (result.message && !isAuthError) {
            showToast(result.message, 'error');
          }
          isLoading.value = false;
          isUserFetched.value = false;
        }
      } catch (error) {
        // Handle timeout or network errors
        isLoading.value = false;
        isUserFetched.value = false;

        // Only show error if it's not a timeout (timeouts are expected in some cases)
        if (error.message !== 'Request timeout') {
          // Don't show toast for network errors to avoid spam
        }
      } finally {
        fetchPromise = null; // Clear promise when done
      }
    })();

    return fetchPromise;
  }

  /**
   * Refresh user data after token refresh
   * This is called automatically after successful token refresh
   */
  async function refreshUserData() {
    try {
      // Force refresh to get latest user data
      await fetchCurrentUser(true);
    } catch (error) {
      // Don't throw - this is a background refresh
    }
  }

  /**
   * BACKGROUND REFRESH: Refresh cached data in background if stale
   * This ensures data stays fresh without blocking the UI
   * CRITICAL FIX: Now checks user-specific cache
   */
  async function refreshIfStale() {
    if (!isUserFetched.value || !user.id) {
      return; // No data to refresh
    }

      try {
        // Get user-specific timestamp key
        const { timestampKey } = getStorageKeys(user.id);
        if (!timestampKey) return;

        // Check both storages
        const storage = getStorageForCache();
        const timestamp = storage.getItem(timestampKey);
        if (timestamp) {
          const age = Date.now() - Number.parseInt(timestamp, 10);
          if (age >= CACHE_DURATION) {
            // Cache is stale, refresh in background
            fetchCurrentUser(true).catch((error) => {
              // Silent fail - keep using cached data
            });
          }
        }
      } catch (error) {
        // Error checking cache staleness
      }
  }

  // Cleanup old cache entries on store initialization (runs once)
  cleanupOldCacheEntries();

  return { user, updateUser, getUser, reset, fetchCurrentUser, refreshUserData, refreshIfStale, isLoading, isUserFetched, cleanupOldCacheEntries }
})
